import ApiClient from './apiClient';
import { logger } from '../logging';

const SSE_TIMEOUT_MS = 120000;

export interface StartOctopusLoadSseOptions {
  lookbackDays?: number;
}

/**
 * Start SSE connection to api/connect/load/octopus to load or refresh consumption data.
 * Dispatches `loadingProgress` (detail: { percent_complete, loaded_to_date, type }) and
 * `loadingComplete` on window. Uses a timeout and closes the connection on error.
 *
 * @param userId - User ID (GUID) to load data for
 * @param options - Optional settings (e.g. lookback_days for the API query)
 */
export function startOctopusLoadSse(
  userId: string,
  options?: StartOctopusLoadSseOptions
): void {
  if (!userId) return;

  try {
    const params = new URLSearchParams({ id: userId });
    if (options?.lookbackDays != null) {
      params.set('lookback_days', String(options.lookbackDays));
    }
    const url = `${ApiClient.getCurrentBaseUrl()}/api/connect/load/octopus?${params.toString()}`;
    const eventSource = new EventSource(url);
    let timeout: ReturnType<typeof setTimeout>;

    const resetTimeout = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        logger.warning('SSE connection timeout - closing connection', undefined, {
          sourceFile: 'loadOctopusSse.ts',
          sourceClass: 'startOctopusLoadSse',
          sourceLayer: 'Utils',
          userId,
        });
        eventSource.close();
      }, SSE_TIMEOUT_MS);
    };

    eventSource.onmessage = function (event: MessageEvent) {
      resetTimeout();
      try {
        const receivedData = JSON.parse(event.data);
        if (receivedData.percent_complete !== undefined) {
          window.dispatchEvent(
            new CustomEvent('loadingProgress', {
              detail: {
                percent_complete: receivedData.percent_complete,
                loaded_to_date: receivedData.loaded_to_date,
                type: receivedData.type,
              },
            })
          );
        }
        if (receivedData.load_complete) {
          clearTimeout(timeout);
          eventSource.close();
          window.dispatchEvent(new CustomEvent('loadingComplete'));
        }
      } catch (error) {
        logger.error('Failed to parse SSE message payload', error, {
          sourceFile: 'loadOctopusSse.ts',
          sourceClass: 'startOctopusLoadSse',
          sourceLayer: 'Utils',
          userId,
        });
        clearTimeout(timeout);
        eventSource.close();
      }
    };

    eventSource.onerror = function () {
      logger.error('SSE connection error while loading Octopus data', undefined, {
        sourceFile: 'loadOctopusSse.ts',
        sourceClass: 'startOctopusLoadSse',
        sourceLayer: 'Utils',
        userId,
        url,
      });
      clearTimeout(timeout);
      eventSource.close();
    };

    resetTimeout();
    setTimeout(() => {
      clearTimeout(timeout);
      eventSource.close();
    }, SSE_TIMEOUT_MS);
  } catch (error) {
    logger.error('Failed to start Octopus SSE load', error, {
      sourceFile: 'loadOctopusSse.ts',
      sourceClass: 'startOctopusLoadSse',
      sourceLayer: 'Utils',
      userId,
    });
  }
}
